import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import i18n from "@/lib/i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, object = user
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(false);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        await api.post("/auth/login", { email, password });
        const me = await refresh();
        if (!me) throw new Error(i18n.t("Accesso riuscito ma la sessione non è stata salvata. Abilita i cookie e riprova."));
        return me;
    };

    const register = async (payload) => {
        await api.post("/auth/register", payload);
        const me = await refresh();
        if (!me) throw new Error(i18n.t("Registrazione riuscita ma la sessione non è stata salvata. Abilita i cookie e riprova."));
        return me;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        setUser(false);
    };

    // Realtime: WebSocket connection that broadcasts chat events as window events
    useEffect(() => {
        if (!user || user.role === "admin") return;
        let ws;
        let pingTimer;
        let reconnectTimer;
        let closed = false;

        const connect = () => {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            const wsUrl = base.replace(/^http/, "ws") + "/api/ws";
            try {
                ws = new WebSocket(wsUrl);
            } catch {
                return;
            }
            ws.onopen = () => {
                pingTimer = setInterval(() => {
                    if (ws?.readyState === WebSocket.OPEN) ws.send("ping");
                }, 25000);
            };
            ws.onmessage = (e) => {
                try {
                    const data = JSON.parse(e.data);
                    window.dispatchEvent(new CustomEvent("fipro:ws", { detail: data }));
                } catch {}
            };
            ws.onclose = () => {
                clearInterval(pingTimer);
                if (!closed) reconnectTimer = setTimeout(connect, 3000);
            };
            ws.onerror = () => { try { ws.close(); } catch {} };
        };
        connect();

        return () => {
            closed = true;
            clearInterval(pingTimer);
            clearTimeout(reconnectTimer);
            try { ws && ws.close(); } catch {}
        };
    }, [user]);

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
